import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {forkJoin, Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

/**
* AI:
* Status: 'in progress'
* Type: Component
* SubType: ProtectedComponent
* Reason: Operational Quittierungs-inbox — global-scope inputs (Periode/Gesellschaft), acknowledged
*         output, keyboard throughput, text search + pagination, bulk-result feedback, and the
*         per-row "Warum diese Zahl?" Herleitung (FE-1 inbox side, FE-6, FE-7 / TM-1000001915).
*/

import {CnReconciliationApiService} from '../../../service/api/cn-reconciliation-api/cn-reconciliation-api.service';
import {CommissionExpectationService} from '../../../service/api/commission-expectation/commission-expectation.service';
import {CnActivityFilterDto} from '../../../model/dto/api/cn-activity-filter.dto';
import {CommissionExpectationDto} from '../../../model/dto/entity/commission-expectation/commission-expectation.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
* Wire shape of one row from GET /cn/activity (backend CnActivityDto).
*
* The backend serialises the {@code kind} / {@code acknowledgeStatus} enums by NAME, the
* {@code bookingPeriod} YearMonth as "YYYY-MM", {@code acknowledgedAt} as an ISO-8601 instant and
* exposes the partner (Gesellschaft) display name as {@code partnerName}. The shipped
* CommissionActivityDto (entity DTO) does NOT match this wire contract (no partnerName, numeric
* enums), so the inbox types the rows against this local interface instead.
*/
interface CnActivityRow {
    id: number;
    /** Activity kind enum NAME (NEW_COMMISSION / STORNO / RECONCILIATION). */
    kind: string;
    /** Acknowledge-status enum NAME (OPEN / ACKNOWLEDGED). */
    acknowledgeStatus: string;
    acknowledgedBy?: string | null;
    /** ISO-8601 timestamp of the acknowledgement. */
    acknowledgedAt?: string | null;
    note?: string | null;
    partnerId?: number | null;
    /** Partner (Gesellschaft) display name. */
    partnerName?: string | null;
    contractId?: number | null;
    /** Versicherungsschein-Nummer (VSNR) of the underlying contract, when the backend resolves it. */
    contractVsnr?: string | null;
    /** Tarif / product display name, when resolvable (used by the text search). */
    tariffName?: string | null;
    /** Kunde / customer display name, when resolvable (shown in the Herleitung panel). */
    customerName?: string | null;
    amount?: number | null;
    /** Booking period "YYYY-MM". */
    bookingPeriod?: string | null;
    statementLineId?: number | null;
    reconciliationId?: number | null;
    expectationId?: number | null;
}

/** A sortable column key on the activity row. */
type SortColumn = 'amount' | 'partnerName' | 'bookingPeriod' | 'kind';

/** Lazy-loaded Herleitung state for one expanded row. */
interface TraceState {
    /** True while the expectation/trace is being fetched. */
    loading: boolean;
    /** Resolved calculationTrace JSON string, or null when none exists. */
    trace: string | null;
    grossAmount?: number;
    netAmount?: number;
    /** True once a fetch attempt completed (success OR a definitive "no trace"). */
    loaded: boolean;
}

/**
* Provisions-Quittierungs-Eingangskorb (P3), hosted by CnDashboardPage.
*
* Operational commission inbox for Sachbearbeitung / Controlling. The dashboard scopes the inbox via
* the global Periode ({@link period}) and Gesellschaft ({@link partnerId}) @Inputs.
*
* <p><b>Server-side paging (TM-1000001915 (4)):</b> the table is a PrimeNG lazy table — it loads ONE page
* at a time via CnReconciliationApiService.listActivitiesPaged() (GET /cn/activity/page). The status /
* Art facets, the Periode + Gesellschaft scope, the sort and the paging are ALL applied server-side. The
* Offen / Quittiert KPI tiles are read from GET /cn/activity/count (status-scoped) — NOT derived from the
* loaded page (regression guard), so they always reflect the full scoped counts regardless of the active
* facet or page. The free-text search (VSNR / Gesellschaft / Tarif) matches resolved fields that are not
* server-filterable columns, so it narrows the CURRENTLY LOADED PAGE client-side only (a documented
* limitation — a hint tells the user to page or use the Status/Art facets for cross-page matches).
* Emits {@link acknowledged} after every single AND bulk acknowledge so the dashboard can live-refresh.</p>
*
* FE-6 (throughput):
*  - Keyboard: ArrowUp/Down move row focus within the page, PageUp/PageDown switch server pages,
*    Space toggles select, "a" selects all open (on the page), "q"/Enter quittieren (via the existing
*    dialog), Esc clears the selection / cancels the dialog. A small legend documents the shortcuts.
*  - Bulk: ONE confirm dialog; afterwards the acknowledge-bulk result ("3 quittiert, 1 übersprungen").
*    Select-all is page-scoped (only the OPEN rows of the loaded page).
*
* FE-7 (Herleitung):
*  - Per-row "Warum diese Zahl?" expander reusing <mvs-cn-expectation-trace>, fetched lazily from the
*    activity's expectation; otherwise a clear "Keine Herleitung verfügbar". Contract/VSNR + Kunde are
*    surfaced in the row and in the panel.
*  - A 0,00 EUR activity gets a gentle confirm warning before quittieren.
*
* UI states: loading / ready / empty / error / busy are handled explicitly.
*/
@Component({
    selector: 'mvs-cn-activity-inbox',
    templateUrl: './cn-activity-inbox.component.html',
    styleUrls: ['./cn-activity-inbox.component.scss'],
    standalone: false
})
export class CnActivityInboxComponent implements OnInit, OnChanges, OnDestroy {

    /** Fallback acknowledger when no session user is resolvable (last resort only). */
    static readonly DEFAULT_USER = 'Sachbearbeitung';

    /** Debounce window (ms) for the free-text search so paging / focus are not reset per keystroke. */
    static readonly SEARCH_DEBOUNCE_MS = 280;

    /** Acknowledge-status enum NAMES as sent to / received from the backend. */
    static readonly STATUS_OPEN = 'OPEN';
    static readonly STATUS_ACKNOWLEDGED = 'ACKNOWLEDGED';

    /** Default page size for the paginated table. */
    static readonly DEFAULT_ROWS = 10;

    // ----- Global scope (FE-1, supplied by the host dashboard) -----------------------------------
    /** Global Periode "YYYY-MM" scope; the list + the KPI sub-counts are scoped to it. */
    @Input() period?: string | null;
    /** Global Gesellschaft (partner) id scope; the list + the KPI sub-counts are scoped to it. */
    @Input() partnerId?: number | null;

    /**
     * Emitted after a single OR bulk acknowledge succeeds, so the host dashboard can re-load its
     * totals / controlling / protocol (live refresh, FE-1).
     */
    @Output() acknowledged = new EventEmitter<void>();

    // ----- Sub-filters (string enum NAMES, applied client-side over the scoped set) --------------
    activeStatusFilter: 'ALL' | 'OPEN' | 'ACKNOWLEDGED' = 'ALL';
    // 'ALL' is the money work-queue default: the backend scopes it to the money-kinds (NEW_COMMISSION +
    // STORNO) and EXCLUDES RECONCILIATION. The explicit RECONCILIATION facet is the way to see the
    // Soll-vs-Ist reconciliation findings; selecting it sends kind=RECONCILIATION to the backend.
    activeKindFilter: 'ALL' | 'NEW_COMMISSION' | 'STORNO' | 'RECONCILIATION' = 'ALL';
    /** Free-text search over VSNR / Gesellschaft / Tarif. */
    searchTerm: string = '';
    /** Emits the raw search term on every keystroke; debounced before re-filtering (FE-6). */
    private readonly searchTerm$ = new Subject<string>();
    /** Subscription of the debounced search pipeline. */
    private searchSubscription?: Subscription;

    // ----- Row data (ONE server page at a time, TM-1000001915 (4)) ----------
    /** The raw current server page of rows (before the client-side free-text narrowing). */
    pageRows: CnActivityRow[] = [];
    /**
     * The visible rows = the current server page narrowed by the free-text search. Status / Art /
     * period / Gesellschaft / sort / paging are all server-side; only the text search is client-side
     * (and only within this loaded page).
     */
    rows: CnActivityRow[] = [];
    listLoading: boolean = false;
    listError: string | null = null;

    // ----- Server-side pagination (lazy) -----
    /** 0-based offset of the current page (PrimeNG lazy `first`). */
    first: number = 0;
    rowsPerPage: number = CnActivityInboxComponent.DEFAULT_ROWS;
    /** Total rows matching the server-side facets (drives `[totalRecords]`). */
    totalRecords: number = 0;

    // ----- Keyboard focus -----
    /** Index (within {@link rows}) of the keyboard-focused row, or -1 when none. */
    focusedIndex: number = -1;

    // ----- Sorting (persists across filter, search and pagination changes) -----
    sortColumn: SortColumn = 'bookingPeriod';
    sortAscending: boolean = false;

    // ----- Selection (bulk) -----
    selectedRows: CnActivityRow[] = [];

    // ----- KPI counters (scoped sub-counts derived from the scoped set) -----
    openCount: number | null = null;
    acknowledgedCount: number | null = null;
    countsLoading: boolean = false;

    // ----- Confirm dialog -----
    confirmVisible: boolean = false;
    /** The rows the dialog will acknowledge (1 for single, N for bulk). */
    confirmRows: CnActivityRow[] = [];
    confirmNote: string = '';
    acknowledgeBusy: boolean = false;
    acknowledgeError: string | null = null;
    /** Gentle warning shown when at least one row in the dialog has a 0,00 EUR amount (FE-7). */
    zeroAmountWarning: boolean = false;

    // ----- Bulk result feedback (FE-6) -----
    /** Plain-language German summary of the last bulk acknowledge ("3 quittiert, 1 übersprungen"). */
    bulkResultMessage: string | null = null;

    // ----- Herleitung ("Warum diese Zahl?") per-row state (FE-7) -----
    /** Id of the row whose Herleitung panel is currently expanded, or null. */
    expandedRowId: number | null = null;
    /** Lazily-loaded trace state keyed by activity id. */
    traceStates: { [activityId: number]: TraceState } = {};

    // ----- Resolved current user -----
    currentUserName: string | null = null;

    initialized: boolean = false;

    constructor(
        private reconciliationApiService: CnReconciliationApiService,
        private expectationService: CommissionExpectationService,
        private coreService: MvsCoreService,
        // TM-1000001915 (d): used to scope the keyboard shortcuts to the inbox subtree so they never
        // fire when focus is elsewhere on the dashboard.
        private elementRef: ElementRef<HTMLElement>
    ) {
    }

    ngOnInit(): void {
        this.resolveCurrentUserName();
        this.subscribeToDebouncedSearch();
        this.initialized = true;
        // The lazy p-table fires its first (onLazyLoad) on init, which loads page 0; the scoped KPI
        // counts are loaded explicitly (they must NOT come from a single page — regression guard).
        this.reloadCounts();
    }

    /**
    * Debounce the free-text search: only re-filter THIS page client-side ~280ms after the user stops
    * typing, and never when the term has not actually changed (FE-6 MAJOR). The search narrows the
    * currently-loaded server page only (see {@link displayRows}); it does not re-query the server.
    */
    private subscribeToDebouncedSearch(): void {
        this.searchSubscription = this.searchTerm$
            .pipe(
                debounceTime(CnActivityInboxComponent.SEARCH_DEBOUNCE_MS),
                distinctUntilChanged()
            )
            .subscribe(() => this.afterPageNarrowed());
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
        this.searchTerm$.complete();
    }

    /**
    * React to a changed global scope (Periode / Gesellschaft): reload page 0 + the scoped KPI counts
    * (FE-1). The status / Art sub-filters and the persisted sort are preserved across the reload.
    */
    ngOnChanges(changes: SimpleChanges): void {
        if (this.initialized && (changes['period'] || changes['partnerId'])) {
            this.first = 0;
            this.reload();
        }
    }

    // =====================================================================================
    // Sub-filters (applied client-side over the scoped set)
    // =====================================================================================

    /**
    * Switch the acknowledge-status sub-filter. Now a SERVER-SIDE facet (TM-1000001915 (4)): re-query
    * page 0 with the new status. The sort persists. The KPI tiles stay unchanged (they always show the
    * full scoped Offen / Quittiert counts regardless of the active status facet).
    */
    setStatusFilter(filter: 'ALL' | 'OPEN' | 'ACKNOWLEDGED'): void {
        this.activeStatusFilter = filter;
        this.first = 0;
        this.loadPage();
    }

    /**
    * Switch the Art (kind) sub-filter. A SERVER-SIDE facet (TM-1000001915 (4)): re-query page 0. 'ALL'
    * is the money work-queue (the backend defaults it to NEW_COMMISSION + STORNO and excludes
    * RECONCILIATION). RECONCILIATION is the explicit facet for the Soll-vs-Ist reconciliation findings —
    * selecting it passes kind=RECONCILIATION to the backend (see {@link buildPagedFilter}).
    */
    setKindFilter(filter: 'ALL' | 'NEW_COMMISSION' | 'STORNO' | 'RECONCILIATION'): void {
        this.activeKindFilter = filter;
        this.first = 0;
        this.loadPage();
    }

    /**
    * React to a text-search change (VSNR / Gesellschaft / Tarif). Pushed through a debounce so the
    * page / focus / sort are not reset on every keystroke; the actual re-filter happens client-side
    * once typing settles. The sort persists.
    */
    onSearchChange(): void {
        this.searchTerm$.next(this.searchTerm);
    }

    /**
    * Clear the text search. This is an explicit action, so it re-narrows immediately (no debounce).
    */
    clearSearch(): void {
        this.searchTerm = '';
        this.searchTerm$.next('');
        this.afterPageNarrowed();
    }

    // =====================================================================================
    // Data loading
    // =====================================================================================

    /**
    * Build the server-side filter for the paged inbox query from the current scope + sub-filters + sort.
    * Status / Art / period / Gesellschaft / sort / paging are ALL applied server-side (TM-1000001915 (4));
    * the free-text search is NOT a server facet (it matches resolved VSNR / Tarif / Kunde which are not QL
    * columns) and so narrows the loaded page client-side instead.
    */
    private buildPagedFilter(): CnActivityFilterDto {
        const filter: CnActivityFilterDto = {
            page: this.currentPageIndex,
            size: this.rowsPerPage,
            sortField: this.sortColumn,
            sortOrder: this.sortAscending ? 1 : -1
        };
        if (this.partnerId !== undefined && this.partnerId !== null) {
            filter.partnerId = this.partnerId;
        }
        if (this.period) {
            filter.period = this.period;
        }
        if (this.activeStatusFilter !== 'ALL') {
            filter.acknowledgeStatus = this.activeStatusFilter as unknown as CnActivityFilterDto['acknowledgeStatus'];
        }
        if (this.activeKindFilter !== 'ALL') {
            // The facet value is already the backend enum NAME (NEW_COMMISSION / STORNO / RECONCILIATION);
            // the API service forwards a string `kind` verbatim. 'ALL' stays unset → money-kinds default.
            filter.kind = this.activeKindFilter as unknown as CnActivityFilterDto['kind'];
        }
        return filter;
    }

    /** 0-based page index derived from the lazy `first` offset + the current page size. */
    private get currentPageIndex(): number {
        return this.rowsPerPage > 0 ? Math.floor(this.first / this.rowsPerPage) : 0;
    }

    /**
    * Reload the current page AND the scoped KPI counts (used on scope change + after an acknowledge).
    */
    reload(): void {
        this.reloadCounts();
        this.loadPage();
    }

    /**
    * Load ONE server page for the current scope + sub-filters + sort + paging. The free-text search then
    * narrows that page client-side. The KPI tiles are NOT touched here (regression guard — they come from
    * {@link reloadCounts}).
    */
    loadPage(): void {
        this.listLoading = true;
        this.listError = null;

        this.reconciliationApiService.listActivitiesPaged(this.buildPagedFilter()).subscribe({
            next: (pageDto) => {
                this.pageRows = (pageDto?.content as unknown as CnActivityRow[]) ?? [];
                this.totalRecords = pageDto?.totalElements ?? this.pageRows.length;
                this.afterPageNarrowed();
                this.listLoading = false;
            },
            error: (err) => {
                this.pageRows = [];
                this.rows = [];
                this.selectedRows = [];
                this.totalRecords = 0;
                this.listLoading = false;
                this.focusedIndex = -1;
                this.listError = cnErrorMessage(err);
            }
        });
    }

    /**
    * PrimeNG lazy-load callback ([lazy]=true / (onLazyLoad)). Translates the table's first / rows /
    * sortField / sortOrder into a server page request (TM-1000001915 (4)). PrimeNG's
    * {@code TableLazyLoadEvent} types {@code sortField} as {@code string | string[]} (multi-sort) and
    * {@code sortOrder} / {@code first} / {@code rows} as optional, so they are narrowed defensively here.
    */
    onLazyLoad(event: {
        first?: number;
        rows?: number | null;
        sortField?: string | string[] | null;
        sortOrder?: number | null;
    }): void {
        this.first = event.first ?? 0;
        this.rowsPerPage = event.rows ?? this.rowsPerPage;
        const sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
        if (sortField) {
            this.sortColumn = sortField as SortColumn;
            this.sortAscending = (event.sortOrder ?? 1) >= 0;
        }
        this.loadPage();
    }

    /**
    * Load the scoped Offen / Quittiert KPI tiles from GET /cn/activity/count (status-scoped), NEVER from
    * a single page (TM-1000001915 (4) regression guard). Honours the same Periode + Gesellschaft scope as
    * the rows.
    */
    reloadCounts(): void {
        this.countsLoading = true;
        const partnerId = (this.partnerId !== undefined && this.partnerId !== null) ? this.partnerId : undefined;
        const period = this.period ?? undefined;
        forkJoin({
            open: this.reconciliationApiService.countActivities(CnActivityInboxComponent.STATUS_OPEN, partnerId, period),
            acknowledged: this.reconciliationApiService.countActivities(
                CnActivityInboxComponent.STATUS_ACKNOWLEDGED, partnerId, period)
        }).subscribe({
            next: (result) => {
                this.openCount = result.open?.count ?? 0;
                this.acknowledgedCount = result.acknowledged?.count ?? 0;
                this.countsLoading = false;
            },
            error: () => {
                this.openCount = null;
                this.acknowledgedCount = null;
                this.countsLoading = false;
            }
        });
    }

    /**
    * Manual refresh (FE-1 "Aktualisieren" affordance hosted in the inbox header).
    */
    manualRefresh(): void {
        this.bulkResultMessage = null;
        this.reload();
    }

    /**
    * Re-derive the visible {@link rows} by narrowing the loaded server page with the free-text search,
    * then drop stale selections and clamp the keyboard focus. No server round-trip. Server-side sort /
    * paging already ordered + sized the page, so no client sort is applied here.
    */
    private afterPageNarrowed(): void {
        const term = this.searchTerm.trim().toLowerCase();
        this.rows = term.length > 0
            ? this.pageRows.filter((row) => this.matchesSearch(row, term))
            : [...this.pageRows];

        // Keep only still-present selections.
        const presentIds = new Set(this.rows.map((row) => row.id));
        this.selectedRows = this.selectedRows.filter((row) => presentIds.has(row.id));

        this.focusedIndex = this.rows.length > 0 ? 0 : -1;
    }

    /**
    * Whether a row matches the free-text search across VSNR / Gesellschaft / Tarif (client-side, within
    * the loaded page only — see {@link loadPage}).
    */
    private matchesSearch(row: CnActivityRow, term: string): boolean {
        const haystack = [
            row.contractVsnr,
            row.partnerName,
            row.tariffName
        ].filter((part) => !!part).join(' ').toLowerCase();
        return haystack.includes(term);
    }

    // =====================================================================================
    // Sorting (persists across filter, search and pagination changes)
    // =====================================================================================

    /**
    * Toggle / set the SERVER-SIDE sort column (TM-1000001915 (4)). Clicking the active column flips
    * direction; a new column starts ascending. The sort is sent to the server and the page is re-queried
    * from page 0 so the ordering spans the whole result set, not just the loaded page.
    */
    sortBy(column: SortColumn): void {
        if (this.sortColumn === column) {
            this.sortAscending = !this.sortAscending;
        } else {
            this.sortColumn = column;
            this.sortAscending = true;
        }
        this.first = 0;
        this.loadPage();
    }

    /**
    * The Font Awesome sort icon for a column header (neutral / asc / desc).
    */
    sortIcon(column: SortColumn): string {
        if (this.sortColumn !== column) {
            return 'fa-regular fa-sort';
        }
        return this.sortAscending ? 'fa-regular fa-sort-up' : 'fa-regular fa-sort-down';
    }

    // =====================================================================================
    // Selection (bulk)
    // =====================================================================================

    /**
    * The OPEN rows of the current (filtered) set — the only rows that may be bulk-acknowledged.
    */
    get selectableRows(): CnActivityRow[] {
        return this.rows.filter((row) => this.isOpen(row));
    }

    /**
    * Whether every selectable (OPEN) row in the current filter is selected.
    */
    get allSelectableSelected(): boolean {
        const selectable = this.selectableRows;
        if (selectable.length === 0) {
            return false;
        }
        const selectedIds = new Set(this.selectedRows.map((row) => row.id));
        return selectable.every((row) => selectedIds.has(row.id));
    }

    /**
    * Select-all / clear-all within the current filter (OPEN rows only).
    */
    toggleSelectAll(): void {
        if (this.allSelectableSelected) {
            this.selectedRows = [];
        } else {
            this.selectedRows = [...this.selectableRows];
        }
    }

    /**
    * Whether a given row is currently selected.
    */
    isSelected(row: CnActivityRow): boolean {
        return this.selectedRows.some((selected) => selected.id === row.id);
    }

    /**
    * Toggle the selection state of a single OPEN row.
    */
    toggleRowSelection(row: CnActivityRow): void {
        if (!this.isOpen(row)) {
            return;
        }
        if (this.isSelected(row)) {
            this.selectedRows = this.selectedRows.filter((selected) => selected.id !== row.id);
        } else {
            this.selectedRows = [...this.selectedRows, row];
        }
    }

    // =====================================================================================
    // Keyboard support (FE-6)
    // =====================================================================================

    /**
    * Keyboard handler for the inbox throughput shortcuts (FE-6), SCOPED to the inbox (TM-1000001915 (d)):
    *   ArrowUp / ArrowDown     — move the focused row, keeping it on the visible page.
    *   PageUp / PageDown       — move the focus by a whole page.
    *   Space                   — toggle the selection of the focused (OPEN) row.
    *   a                       — select all OPEN rows in the current filter.
    *   q / Enter               — open the quittieren dialog (focused row, else the bulk selection).
    *   Esc                     — cancel the open dialog; otherwise clear the current selection.
    *
    * <p><b>Focus scoping (d):</b> although this is a host listener (so it already only catches events
    * bubbling through THIS component's subtree, never the rest of the dashboard), the shortcuts are
    * additionally guarded so they ONLY act when the event actually originated inside the inbox subtree
    * AND focus is not in a text-entry control or behind a dialog. This prevents an accidental
    * acknowledge / select when focus happens to sit elsewhere.</p>
    *
    * Typing into the search box / note textarea is never hijacked. Once the dialog is open only Esc
    * (cancel) is honoured so shortcuts cannot fire behind the modal.
    */
    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (!this.initialized) {
            return;
        }

        // (d) Only act when the keystroke originated inside the inbox subtree. A host listener already
        // limits us to this component, but we re-assert it explicitly so the contract is obvious and the
        // shortcuts can never act on a stray event re-dispatched onto the host.
        if (!this.isWithinInbox(event.target)) {
            return;
        }

        // Esc: cancel the dialog while it is open; otherwise clear the current bulk selection (d).
        if (event.key === 'Escape') {
            if (this.confirmVisible) {
                if (!this.acknowledgeBusy) {
                    this.cancelConfirm();
                    event.preventDefault();
                }
                return;
            }
            if (this.selectedRows.length > 0) {
                this.clearSelection();
                event.preventDefault();
            }
            return;
        }

        // While the dialog is open, swallow the table shortcuts.
        if (this.confirmVisible) {
            return;
        }

        // Do not hijack typing in inputs / textareas / selects.
        if (this.isTextEntryTarget(event.target)) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                this.moveFocus(1);
                event.preventDefault();
                break;
            case 'ArrowUp':
                this.moveFocus(-1);
                event.preventDefault();
                break;
            case 'PageDown':
                this.goToPage(1);
                event.preventDefault();
                break;
            case 'PageUp':
                this.goToPage(-1);
                event.preventDefault();
                break;
            case ' ':
            case 'Spacebar':
                if (this.focusedRow) {
                    this.toggleRowSelection(this.focusedRow);
                }
                event.preventDefault();
                break;
            case 'a':
            case 'A':
                this.toggleSelectAll();
                event.preventDefault();
                break;
            case 'q':
            case 'Q':
            case 'Enter':
                this.quittierenFromKeyboard();
                event.preventDefault();
                break;
            default:
                break;
        }
    }

    /**
    * Whether the keyboard event originated inside this inbox's DOM subtree (TM-1000001915 (d)). Guards
    * the shortcuts so they never act when focus is elsewhere on the dashboard.
    */
    private isWithinInbox(target: EventTarget | null): boolean {
        const host = this.elementRef.nativeElement;
        const el = target as Node | null;
        return el != null && host != null && host.contains(el);
    }

    /**
    * Clear the current bulk selection (TM-1000001915 (d): Escape comfort key). Keyboard focus and paging
    * are intentionally preserved so the operator keeps their place in the list.
    */
    clearSelection(): void {
        this.selectedRows = [];
    }

    /**
    * Whether the event target is a text-entry control we must not hijack.
    */
    private isTextEntryTarget(target: EventTarget | null): boolean {
        const el = target as HTMLElement | null;
        if (!el || !el.tagName) {
            return false;
        }
        const tag = el.tagName.toLowerCase();
        return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    }

    /**
    * The currently keyboard-focused row, or null.
    */
    get focusedRow(): CnActivityRow | null {
        if (this.focusedIndex < 0 || this.focusedIndex >= this.rows.length) {
            return null;
        }
        return this.rows[this.focusedIndex];
    }

    /**
    * Whether a given row is the keyboard-focused row (used to highlight it in the template).
    */
    isFocused(row: CnActivityRow): boolean {
        return this.focusedRow?.id === row.id;
    }

    /**
    * Move the keyboard focus by delta within the CURRENT server page ({@link rows}), clamping to its
    * bounds. Page changes are handled separately via {@link goToPage} (PageUp / PageDown) so the focus
    * never indexes past the loaded page (TM-1000001915 (4)).
    */
    private moveFocus(delta: number): void {
        if (this.rows.length === 0) {
            this.focusedIndex = -1;
            return;
        }
        const next = this.focusedIndex < 0 ? 0 : this.focusedIndex + delta;
        this.focusedIndex = Math.max(0, Math.min(this.rows.length - 1, next));
    }

    /**
    * Navigate to the previous / next SERVER page (PageUp / PageDown), clamped to the available range.
    * Re-queries that page; the focus resets to its first row once the page loads (TM-1000001915 (4)).
    */
    private goToPage(direction: number): void {
        const lastPageIndex = this.rowsPerPage > 0
            ? Math.max(0, Math.ceil(this.totalRecords / this.rowsPerPage) - 1)
            : 0;
        const targetPage = Math.max(0, Math.min(lastPageIndex, this.currentPageIndex + direction));
        const targetFirst = targetPage * this.rowsPerPage;
        if (targetFirst === this.first) {
            return;
        }
        this.first = targetFirst;
        this.loadPage();
    }

    /**
    * Keyboard "q"/Enter dispatch: prefer the bulk selection if any OPEN rows are selected, else the
    * focused OPEN row. Does nothing when neither applies.
    */
    private quittierenFromKeyboard(): void {
        const openSelection = this.selectedRows.filter((row) => this.isOpen(row));
        if (openSelection.length > 0) {
            this.openConfirmBulk();
            return;
        }
        if (this.focusedRow && this.isOpen(this.focusedRow)) {
            this.openConfirmSingle(this.focusedRow);
        }
    }

    // =====================================================================================
    // Herleitung — "Warum diese Zahl?" (FE-7)
    // =====================================================================================

    /**
    * Toggle the per-row Herleitung panel. On first expand the row's expectation (and its
    * calculationTrace) is fetched lazily; a row without an expectationId resolves immediately to the
    * "Keine Herleitung verfügbar" state.
    */
    toggleTrace(row: CnActivityRow): void {
        if (this.expandedRowId === row.id) {
            this.expandedRowId = null;
            return;
        }
        this.expandedRowId = row.id;
        if (!this.traceStates[row.id]) {
            this.loadTrace(row);
        }
    }

    /**
    * Whether the Herleitung panel for a row is expanded.
    */
    isTraceExpanded(row: CnActivityRow): boolean {
        return this.expandedRowId === row.id;
    }

    /**
    * The lazily-loaded trace state for a row (may be undefined before first expand).
    */
    traceState(row: CnActivityRow): TraceState | undefined {
        return this.traceStates[row.id];
    }

    /**
    * Fetch the CommissionExpectation (and its calculationTrace) for a row via the expectation CRUD
    * service. Without an expectationId there is nothing to fetch — resolve straight to "no trace".
    *
    * The filter uses the PLAIN 'id' attribute name (FilterService auto-resolves it), mirroring the
    * rule-preview fetch path (TM-1000001915 B14).
    */
    private loadTrace(row: CnActivityRow): void {
        if (row.expectationId === undefined || row.expectationId === null) {
            this.traceStates[row.id] = {loading: false, trace: null, loaded: true};
            return;
        }

        this.traceStates[row.id] = {loading: true, trace: null, loaded: false};

        const filters: FilterCriteria[] = [
            FilterCriteria.create('id', FilterCriteria.cOperatorEqual, row.expectationId)
        ];
        const request = ObjectRequestList.createBasic(true, filters, [new Sorting('id', false)]);

        this.expectationService.list(request).subscribe({
            next: (res) => {
                const entry = res.entries?.[0] as CommissionExpectationDto;
                this.traceStates[row.id] = {
                    loading: false,
                    loaded: true,
                    trace: entry?.calculationTrace ?? null,
                    grossAmount: entry?.expectedGrossAmount,
                    netAmount: entry?.expectedNetAmount
                };
            },
            error: () => {
                // A failed fetch is surfaced as "no trace" — never a raw HTTP error in the row.
                this.traceStates[row.id] = {loading: false, trace: null, loaded: true};
            }
        });
    }

    /**
    * Kunde (customer) display label, surfaced from the resolved {@code customerName} DTO field.
    * Falls back to "–" only when the backend could not resolve a customer (null).
    */
    customerLabel(row: CnActivityRow): string {
        return row?.customerName && row.customerName.trim().length > 0 ? row.customerName : '–';
    }

    /**
    * VSNR display label, surfaced from the resolved {@code contractVsnr} DTO field. Falls back to
    * "–" only when the backend could not resolve a VSNR (null).
    */
    vsnrLabel(row: CnActivityRow): string {
        return row?.contractVsnr && row.contractVsnr.trim().length > 0 ? row.contractVsnr : '–';
    }

    /**
    * Tarif display label, surfaced from the resolved {@code tariffName} DTO field. Falls back to
    * "–" only when the backend could not resolve a tariff (null).
    */
    tariffLabel(row: CnActivityRow): string {
        return row?.tariffName && row.tariffName.trim().length > 0 ? row.tariffName : '–';
    }

    // =====================================================================================
    // Confirm dialog (single + bulk)
    // =====================================================================================

    /**
    * Open the confirm dialog for a single OPEN row.
    */
    openConfirmSingle(row: CnActivityRow): void {
        if (!this.isOpen(row)) {
            return;
        }
        this.confirmRows = [row];
        this.openConfirm();
    }

    /**
    * Open the confirm dialog for the current bulk selection (OPEN rows only).
    */
    openConfirmBulk(): void {
        const openSelection = this.selectedRows.filter((row) => this.isOpen(row));
        if (openSelection.length === 0) {
            return;
        }
        this.confirmRows = openSelection;
        this.openConfirm();
    }

    /**
    * Shared dialog opener: reset the note / error, compute the 0,00 EUR gentle warning and show it.
    */
    private openConfirm(): void {
        this.confirmNote = '';
        this.acknowledgeError = null;
        this.bulkResultMessage = null;
        // FE-7: gentle warning if any row in the dialog carries a 0,00 EUR amount.
        this.zeroAmountWarning = this.confirmRows.some((row) => (row.amount ?? 0) === 0);
        this.confirmVisible = true;
    }

    /**
    * Whether the dialog is in bulk mode (more than one row).
    */
    get isBulkConfirm(): boolean {
        return this.confirmRows.length > 1;
    }

    /**
    * The summed amount of the rows in the dialog (bulk summary line).
    */
    get confirmSum(): number {
        return this.confirmRows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
    }

    /**
    * Cancel the dialog WITHOUT any server call.
    */
    cancelConfirm(): void {
        this.confirmVisible = false;
        this.confirmRows = [];
        this.confirmNote = '';
        this.acknowledgeError = null;
        this.zeroAmountWarning = false;
    }

    /**
    * Confirm the acknowledgement: single → acknowledgeActivity, bulk → acknowledgeBulk. The optional
    * Notiz is passed through; the server stamps acknowledgedBy from the authenticated principal
    * (BE-2). On success the scoped set + KPI sub-counts reload, the bulk result is surfaced and the
    * {@link acknowledged} output fires so the dashboard live-refreshes.
    */
    confirmAcknowledge(): void {
        if (this.acknowledgeBusy || this.confirmRows.length === 0) {
            return;
        }

        const note = this.confirmNote && this.confirmNote.trim().length > 0
            ? this.confirmNote.trim()
            : undefined;

        this.acknowledgeBusy = true;
        this.acknowledgeError = null;

        if (this.isBulkConfirm) {
            const ids = this.confirmRows.map((row) => row.id);
            this.reconciliationApiService.acknowledgeBulk(ids, note).subscribe({
                next: (result) => this.onBulkAcknowledgeSuccess(result?.acknowledgedCount ?? 0, result?.skippedCount ?? 0),
                error: (err) => this.onAcknowledgeError(err)
            });
        } else {
            this.reconciliationApiService.acknowledgeActivity(this.confirmRows[0].id, note).subscribe({
                next: () => this.onSingleAcknowledgeSuccess(),
                error: (err) => this.onAcknowledgeError(err)
            });
        }
    }

    /**
    * Post single-acknowledge: close the dialog, clear the selection, reload and notify the host.
    */
    private onSingleAcknowledgeSuccess(): void {
        this.bulkResultMessage = null;
        this.finishAcknowledge();
    }

    /**
    * Post bulk-acknowledge: surface the German result summary ("3 quittiert, 1 übersprungen"),
    * close the dialog, clear the selection, reload and notify the host (FE-6).
    */
    private onBulkAcknowledgeSuccess(acknowledged: number, skipped: number): void {
        const ackPart = `${acknowledged} quittiert`;
        this.bulkResultMessage = skipped > 0 ? `${ackPart}, ${skipped} übersprungen` : ackPart;
        this.finishAcknowledge();
    }

    /**
    * Shared post-acknowledge teardown + reload + host notification.
    */
    private finishAcknowledge(): void {
        this.acknowledgeBusy = false;
        this.confirmVisible = false;
        this.confirmRows = [];
        this.confirmNote = '';
        this.zeroAmountWarning = false;
        this.selectedRows = [];
        // Reload the current page AND the scoped KPI counts so the tiles + rows reflect the acknowledge.
        this.reload();
        this.acknowledged.emit();
    }

    /**
    * Acknowledge failure: surface a friendly German message (never a raw HttpErrorResponse) inside
    * the dialog and keep it open so the user can retry.
    */
    private onAcknowledgeError(err: any): void {
        this.acknowledgeBusy = false;
        this.acknowledgeError = cnErrorMessage(err);
    }

    // =====================================================================================
    // Current user
    // =====================================================================================

    /**
    * Resolve the logged-in user's display name from the um.User service (me()). Kept for display
    * affordances only; the backend stamps acknowledgedBy from the principal (BE-2), so this is not
    * sent with the request. Falls back to null (then DEFAULT_USER) when unavailable.
    */
    private resolveCurrentUserName(): void {
        const userService: any = this.coreService.getCrudService('um.User');
        if (!userService || typeof userService.me !== 'function') {
            this.currentUserName = null;
            return;
        }
        userService.me().subscribe({
            next: (user: any) => {
                this.currentUserName = this.pickUserName(user);
            },
            error: () => {
                this.currentUserName = null;
            }
        });
    }

    /**
    * Choose the best human-readable name from the me() DTO (name → first+last → username).
    */
    private pickUserName(user: any): string | null {
        if (!user) {
            return null;
        }
        if (user.name && String(user.name).trim().length > 0) {
            return String(user.name).trim();
        }
        const composed = [user.firstName, user.lastName].filter((part) => !!part).join(' ').trim();
        if (composed.length > 0) {
            return composed;
        }
        if (user.username && String(user.username).trim().length > 0) {
            return String(user.username).trim();
        }
        return null;
    }

    // =====================================================================================
    // Row helpers (Klartext labels, status)
    // =====================================================================================

    /**
    * Whether a row is OPEN and thus acknowledgeable.
    */
    isOpen(row: CnActivityRow): boolean {
        return row?.acknowledgeStatus === CnActivityInboxComponent.STATUS_OPEN;
    }

    /**
    * Whether a row is already acknowledged.
    */
    isAcknowledged(row: CnActivityRow): boolean {
        return row?.acknowledgeStatus === CnActivityInboxComponent.STATUS_ACKNOWLEDGED;
    }

    /**
    * German Klartext label for an activity kind enum NAME.
    */
    kindLabel(kind: string | null | undefined): string {
        switch (kind) {
            case 'NEW_COMMISSION':
                return 'Neuvertrag';
            case 'STORNO':
                return 'Storno';
            case 'RECONCILIATION':
                return 'Abgleich';
            default:
                return 'Unbekannt';
        }
    }

    /**
    * Gesellschaft display name (falls back to a placeholder when unknown).
    */
    partnerLabel(row: CnActivityRow): string {
        return row?.partnerName && row.partnerName.trim().length > 0 ? row.partnerName : 'Unbekannt';
    }
}
